import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    IconButton,
    Menu,
    MenuItem,
    ListItemText,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Alert,
    InputBase,
    Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Filter, BookmarkCheck, Search, X, Pencil, Trash2, Save } from 'lucide-react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { FilterChip, FilterSearch } from 'components/v2/filters';
import { v2 as v2t, v2Density, v2Tones, V2_FONT_FAMILY } from 'styles/tokens/v2';
import KanbanFilterDrawer from './KanbanFilterDrawer';

const pillSx = (active) => ({
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8125rem',
    height: 36,
    px: 1.5,
    gap: 0.5,
    borderRadius: '10px',
    border: '1px solid',
    borderColor: active ? '#93c5fd' : '#dbe1ea',
    color: active ? '#1d4ed8' : '#475569',
    backgroundColor: active ? '#eff6ff' : '#fff',
    boxShadow: 'none',
    '&:hover': {
        backgroundColor: active ? '#dbeafe' : '#f8fafc',
        borderColor: '#93c5fd',
        boxShadow: 'none',
    },
});

// UI v2: the same pill on v2 tokens (design-reference table toolbar).
const v2PillSx = (active) => ({
    textTransform: 'none',
    fontFamily: V2_FONT_FAMILY,
    fontWeight: 600,
    fontSize: 13,
    height: v2Density.controlHeightPx,
    minHeight: v2Density.controlHeightPx,
    px: 1.5,
    gap: 0.75,
    borderRadius: `${v2Density.radiusPx}px`,
    border: '1px solid',
    borderColor: active ? v2t.primary : v2t.border2,
    color: active ? `${v2Tones.info.fg} !important` : `${v2t.text} !important`,
    backgroundColor: active ? v2Tones.info.bg : v2t.surface,
    boxShadow: 'none',
    '&:hover': {
        backgroundColor: active ? v2Tones.info.bg : v2t.hover,
        borderColor: active ? v2t.primary : v2t.text3,
        boxShadow: 'none',
    },
    '&.Mui-focusVisible': { outline: `2px solid ${v2t.link}`, outlineOffset: 1 },
});

const CountBadge = ({ count, v2 }) => (
    <Box
        component="span"
        sx={{
            minWidth: 18,
            height: 18,
            px: '5px',
            borderRadius: '9px',
            backgroundColor: v2 ? v2t.primary : '#2563eb',
            color: '#fff',
            fontVariantNumeric: v2 ? 'tabular-nums' : undefined,
            fontSize: '0.68rem',
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
    >
        {count}
    </Box>
);

/**
 * Generic toolbar for any Kanban board: Filter, Saved Filters and Search.
 * All state and API calls live in useKanbanFilters; pass its return value as `filters`.
 */
const KanbanToolbar = ({ filters, searchPlaceholder, itemLabel, objectLabel, actions }) => {
    const isV2 = useTheme()?.pearlUi === 'v2';
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [input, setInput] = useState(filters.search);
    const debounceRef = useRef(null);

// Debounce typing so big boards don't re-filter on every keystroke
    useEffect(() => {
        if (input === filters.search) return; // nothing to push
        debounceRef.current = setTimeout(() => filters.setSearch(input), 200);
        return () => clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [input]);

    // External "clear all": cancel any pending timer and reset the box
    useEffect(() => {
        clearTimeout(debounceRef.current);
        setInput(filters.search); // '' after clearAll
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.searchResetKey]);

    const {
        selectedFilter,
        savedFilters,
        loadingFilters,
        activeCount,
        isFiltering,
        visibleRows,
        totalCount,
        objectType,
        notice,
    } = filters;

    const closeMenu = () => setMenuAnchor(null);
    const hasActiveFilter = activeCount > 0 || Boolean(selectedFilter);
    const activeLabel = selectedFilter?.name || `${activeCount} condition${activeCount === 1 ? '' : 's'}`;

    return (
        <>
            <MDBox
                px={2}
                pt={2}
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
                gap={1.5}
            >
                {/* Left: Filter + Saved Filters */}
                <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <Button variant="outlined" onClick={filters.openDrawer} sx={isV2 ? v2PillSx(activeCount > 0) : pillSx(activeCount > 0)}>
                        <Filter size={16} />
                        Filter
                        {activeCount > 0 && <CountBadge count={activeCount} v2={isV2} />}
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        disabled={loadingFilters}
                        sx={isV2 ? v2PillSx(Boolean(selectedFilter)) : pillSx(Boolean(selectedFilter))}
                    >
                        {loadingFilters ? <CircularProgress size={16} color="inherit" /> : <BookmarkCheck size={16} />}
                        Saved filters
                    </Button>

                    {hasActiveFilter && isV2 && (
                        <FilterChip
                            label={activeLabel}
                            selected
                            onRemove={filters.clearFilter}
                            removeLabel="Clear filter"
                        />
                    )}
                    {hasActiveFilter && !isV2 && (
                        <Chip
                            label={activeLabel}
                            size="small"
                            onDelete={filters.clearFilter}
                            sx={{
                                height: 28,
                                fontWeight: 600,
                                color: '#1d4ed8',
                                backgroundColor: '#eff6ff',
                                border: '1px solid #bfdbfe',
                                '& .MuiChip-deleteIcon': { color: '#60a5fa', '&:hover': { color: '#1d4ed8' } },
                            }}
                        />
                    )}
                </MDBox>

                {/* Right: result count, search, extra actions */}
                <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
                    {isFiltering && !filters.filtersLoading && (
                        <MDTypography
                            variant="caption"
                            sx={
                                isV2
                                    ? { color: `${v2t.text3} !important`, fontSize: 13, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }
                                    : { color: '#64748b', whiteSpace: 'nowrap' }
                            }
                        >
                            Showing {visibleRows.length} of {totalCount} {itemLabel}
                            {totalCount === 1 ? '' : 's'}
                        </MDTypography>
                    )}

                    {isV2 ? (
                        <FilterSearch
                            value={input}
                            onChange={setInput}
                            placeholder={searchPlaceholder}
                            width={{ xs: '100%', sm: 260 }}
                        />
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                height: 36,
                                width: { xs: '100%', sm: 260 },
                                px: 1.25,
                                borderRadius: '10px',
                                border: '1px solid #dbe1ea',
                                backgroundColor: '#fff',
                                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                '&:focus-within': { borderColor: '#60a5fa', boxShadow: '0 0 0 3px rgba(96,165,250,0.18)' },
                            }}
                        >
                            <Search size={16} color="#94a3b8" />
                            <InputBase
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={searchPlaceholder}
                                sx={{ flex: 1, fontSize: '0.8125rem', color: '#1e293b' }}
                                inputProps={{ 'aria-label': searchPlaceholder }}
                            />
                            {input && (
                                <IconButton size="small" onClick={() => setInput('')} sx={{ p: 0.25 }}>
                                    <X size={14} color="#94a3b8" />
                                </IconButton>
                            )}
                        </Box>
                    )}

                    {actions}
                </MDBox>
            </MDBox>

            {/* Saved filters menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ style: { maxHeight: 320, width: 300, marginTop: 8 } }}
            >
                <MenuItem disabled sx={{ opacity: '1 !important' }}>
                    <ListItemText
                        primary="Saved Filters"
                        primaryTypographyProps={{ fontWeight: 'bold', fontSize: '0.875rem', color: '#344767' }}
                    />
                </MenuItem>

                {savedFilters.length === 0 ? (
                    <MenuItem disabled>
                        <ListItemText
                            primary="No saved filters"
                            secondary={objectType ? `Create one from the Filter panel` : 'Select an object type'}
                            primaryTypographyProps={{ fontSize: '0.875rem', color: '#7b809a' }}
                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                    </MenuItem>
                ) : (
                    savedFilters.map((f) => (
                        <MenuItem
                            key={f.id}
                            selected={selectedFilter?.id === f.id}
                            onClick={() => {
                                filters.selectSavedFilter(f);
                                closeMenu();
                            }}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 1,
                                pr: 1,
                                '&.Mui-selected': { backgroundColor: '#e3f2fd' },
                            }}
                        >
                            <ListItemText
                                primary={f.name}
                                secondary={f.description || `${f.conditions?.length || 0} condition(s)`}
                                primaryTypographyProps={{
                                    noWrap: true,
                                    fontSize: '0.875rem',
                                    fontWeight: selectedFilter?.id === f.id ? 'medium' : 'regular',
                                }}
                                secondaryTypographyProps={{ noWrap: true, fontSize: '0.75rem' }}
                                sx={{ minWidth: 0 }}
                            />
                            <MDBox display="flex" gap={0.5} flexShrink={0}>
                                <IconButton
                                    size="small"
                                    aria-label="Edit filter"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        closeMenu();
                                        filters.editSavedFilter(f);
                                    }}
                                    sx={{ p: 0.5, color: 'info.main', '&:hover': { backgroundColor: '#e3f2fd' } }}
                                >
                                    <Pencil size={14} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    aria-label="Delete filter"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        closeMenu();
                                        filters.requestDelete(f);
                                    }}
                                    sx={{ p: 0.5, color: '#ef4444', '&:hover': { backgroundColor: '#fef2f2' } }}
                                >
                                    <Trash2 size={14} />
                                </IconButton>
                            </MDBox>
                        </MenuItem>
                    ))
                )}

                {hasActiveFilter && (
                    <MenuItem
                        onClick={() => {
                            filters.clearFilter();
                            closeMenu();
                        }}
                        sx={{ color: '#f44336', borderTop: '1px solid #eef0f3', mt: 0.5 }}
                    >
                        <ListItemText primary="Clear Filter" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 'medium' }} />
                    </MenuItem>
                )}
            </Menu>

            <KanbanFilterDrawer filters={filters} objectLabel={objectLabel} />

            {/* Save / update dialog */}
            <Dialog open={filters.saveDialogOpen} onClose={filters.closeSaveDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ backgroundColor: 'info.main', color: '#fff', py: 2.5 }}>
                    <MDBox display="flex" alignItems="center" gap={1.5}>
                        <MDBox sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '8px', p: 0.75, display: 'flex' }}>
                            <Save size={20} color="#fff" />
                        </MDBox>
                        <MDTypography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                            {filters.editingFilter ? 'Update Filter' : 'Save Filter'}
                        </MDTypography>
                    </MDBox>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <MDBox sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Filter Name"
                            value={filters.filterName}
                            onChange={(e) => filters.setFilterName(e.target.value)}
                            placeholder="e.g., Hot leads this month"
                            autoFocus
                        />
                        <TextField
                            fullWidth
                            label="Description (optional)"
                            value={filters.filterDescription}
                            onChange={(e) => filters.setFilterDescription(e.target.value)}
                            multiline
                            rows={3}
                        />
                    </MDBox>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid #e8ecf0' }}>
                    <Button onClick={filters.closeSaveDialog} sx={{ color: '#64748b', fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={filters.submitFilter}
                        disabled={filters.saving || !filters.filterName.trim()}
                        sx={{
                            backgroundColor: 'info.main',
                            color: '#fff !important',
                            fontWeight: 600,
                            px: 3,
                            borderRadius: 2,
                            '&:hover': { backgroundColor: '#1565c0' },
                        }}
                    >
                        {filters.saving ? <CircularProgress size={20} color="inherit" /> : filters.editingFilter ? 'Update Filter' : 'Save Filter'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={Boolean(filters.deleteTarget)} onClose={filters.cancelDelete} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle>
                    <MDTypography variant="h6" fontWeight="bold" color="error">
                        Delete Filter
                    </MDTypography>
                </DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color="text">
                        Are you sure you want to delete the filter &ldquo;{filters.deleteTarget?.name}&rdquo;? This action cannot be undone.
                    </MDTypography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={filters.cancelDelete} sx={{ color: '#64748b', fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={filters.confirmDelete}
                        disabled={filters.deleting}
                        sx={{ color: 'white !important', backgroundColor: '#ef4444', fontWeight: 600, '&:hover': { backgroundColor: '#dc2626' } }}
                    >
                        {filters.deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Feedback */}
            <Snackbar
                open={notice.open}
                autoHideDuration={4000}
                onClose={(_, reason) => reason !== 'clickaway' && filters.closeNotice()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={filters.closeNotice} severity={notice.severity} sx={{ width: '100%' }}>
                    {notice.message}
                </Alert>
            </Snackbar>
        </>
    );
};

KanbanToolbar.propTypes = {
    filters: PropTypes.object.isRequired, // return value of useKanbanFilters()
    searchPlaceholder: PropTypes.string,
    itemLabel: PropTypes.string, // singular noun for "Showing X of Y leads"
    objectLabel: PropTypes.string, // plural label for the drawer title, e.g. "Leads"
    actions: PropTypes.node, // optional extra controls on the right
};

KanbanToolbar.defaultProps = {
    searchPlaceholder: 'Search...',
    itemLabel: 'item',
    objectLabel: undefined,
    actions: null,
};

export default KanbanToolbar;
